// src/components/Orders/SuppliesPage.js
import React, { useState, useEffect } from 'react';
import SupplyForm from './SupplyForm';
import SupplyList from './SupplyList';
import SupplyCategoryForm from './SupplyCategoryForm';
import SupplyCategoryList from './SupplyCategoryList';
import {
  getSupplies,
  createSupply,
  updateSupply,
  deleteSupply,
  getSupplyCategories,
  createSupplyCategory,
  deleteSupplyCategory
} from '../../api';

const SuppliesPage = () => {
  const [supplies, setSupplies] = useState([]);
  const [supplyCategories, setSupplyCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingSupply, setEditingSupply] = useState(null);

  // =======================
  // CARGAR DATOS
  // =======================

  const loadSupplies = async () => {
    const res = await getSupplies();

    if (res.success) {

      const suppliesWithStock = (res.data || []).map(s => ({
        ...s,
        lowStockThreshold: s.lowStockThreshold ?? 100
      }));

      setSupplies(suppliesWithStock);

    } else {
      console.error('Error al cargar insumos:', res.message);
    }
  };

  const loadCategories = async () => {
    const res = await getSupplyCategories();
    if (res.success) setSupplyCategories(res.data || []);
    else console.error('Error al cargar categorías:', res.message);
  };

  useEffect(() => {
    loadSupplies();
    loadCategories();
  }, []);

  // =======================
  // INSUMOS
  // =======================

  const handleAddSupply = async (supplyData) => {

    const res = await createSupply(supplyData);

    if (res.success) {

      const newSupply = {
        ...res.data,
        lowStockThreshold: res.data?.lowStockThreshold ?? supplyData.lowStockThreshold ?? 100
      };

      setSupplies(prev => [...prev, newSupply]);

    } else {
      console.error('Error al crear insumo:', res.message);
    }
  };

  const handleUpdateSupply = async (supplyData) => {

    const res = await updateSupply(supplyData);

    if (res.success) {

      const updatedSupply = {
        ...res.data,
        lowStockThreshold: res.data?.lowStockThreshold ?? supplyData.lowStockThreshold ?? 100
      };

      setSupplies(prev =>
        prev.map(s => (s?.id === supplyData.id ? updatedSupply : s))
      );

    } else {
      console.error('Error al actualizar insumo:', res.message);
    }
  };

  const handleDeleteSupply = async (id) => {
    const res = await deleteSupply(id);
    if (res.success) {
      setSupplies(prev => prev.filter(s => s?.id !== id));
    } else {
      console.error('Error al eliminar insumo:', res.message);
    }
  };

  const handleEditSupply = (supply) => {

    const safeSupply = {
      ...supply,
      lowStockThreshold: supply?.lowStockThreshold ?? 100
    };

    setEditingSupply(safeSupply);
  };

  const handleCancelEdit = () => {
    setEditingSupply(null);
  };

  const handleSaveSupply = (supplyData) => {
    handleUpdateSupply(supplyData);
    setEditingSupply(null);
  };

  // =======================
  // CATEGORÍAS
  // =======================

  const handleAddCategory = async (categoryData) => {
    const res = await createSupplyCategory(categoryData);

    if (res.success) {

      const newCategory = res.data || { id: Date.now(), name: categoryData.name };

      setSupplyCategories(prev => [...prev, newCategory]);

    } else {
      console.error('Error al crear categoría:', res.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    const res = await deleteSupplyCategory(id);

    if (res.success) {

      setSupplyCategories(prev => prev.filter(c => c?.id !== id));

      // Evitar errores con categoryId undefined
      setSupplies(prev =>
        prev.map(s => ({
          ...s,
          categoryId: s?.categoryId === id ? null : s?.categoryId
        }))
      );

    } else {
      console.error('Error al eliminar categoría:', res.message);
    }
  };

  // =======================
  // FILTRO POR CATEGORÍA
  // =======================

  const filteredSupplies = selectedCategory
    ? supplies.filter(s => (s?.categoryId || 0) === Number(selectedCategory))
    : supplies;

  return (
    <div className="space-y-6 mt-6">

      {/* FORMULARIO Y LISTA DE CATEGORÍAS */}

      <div>
        <SupplyCategoryForm onSubmit={handleAddCategory} />

        <div className="mt-6">
          <SupplyCategoryList
            categories={supplyCategories}
            onDelete={handleDeleteCategory}
          />
        </div>
      </div>

      {/* FORMULARIO DE INSUMOS */}

      <div>
        <SupplyForm
          categories={supplyCategories}
          onSubmit={handleAddSupply}
          editingSupply={editingSupply}
          onSaveSupply={handleSaveSupply}
          onCancelEdit={handleCancelEdit}
        />
      </div>

      {/* LISTA DE INSUMOS */}

      <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">

        <h3 className="text-lg font-semibold text-[#3F1907] mb-3">
          Lista de Insumos
        </h3>

        <div className="mb-4">

          <label className="block text-[#3F1907] text-sm font-bold mb-1">
            Filtrar por Categoría
          </label>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
          >

            <option value="">
              Todas las Categorías
            </option>

            {supplyCategories.map(category => (
              <option
                key={category?.id || category?.name || Date.now()}
                value={category?.id || ''}
              >
                {category?.name || 'Sin nombre'}
              </option>
            ))}

          </select>

        </div>

        <SupplyList
          supplies={filteredSupplies}
          onDelete={handleDeleteSupply}
          onUpdate={handleUpdateSupply}
          categories={supplyCategories}
          onEdit={handleEditSupply}
        />

      </div>

    </div>
  );
};

export default SuppliesPage;